/**
 * Money units and the shared rounding primitive.
 *
 * ## What owns what
 *
 * `chargeLineInclVat` (in `./index`) owns THE RULE — how a price and a quantity
 * become an amount of money. It is `round2(amount × round2(unitInclVat))`,
 * HYP's exact per-line arithmetic, confirmed against real charged orders
 * (PR #175). HYP is the system that actually moves the money, and its payment
 * page can only carry a 2-decimal unit price, so that rounding is unavoidable
 * the moment we send it real products. Matching it is what makes
 * display === charge.
 *
 * This module owns THE UNITS — the types that stop a shekel value reaching a
 * parameter that expects agorot, and the conversions between them.
 *
 * The two are orthogonal on purpose: *which* rule is a question about the
 * outside world (what does HYP do), *how it is enforced* is a question about
 * the compiler. Getting the first right and leaving the second to human care is
 * how the same rounding bug got fixed ten times.
 *
 * ## Why the units are TYPES
 *
 * `number` used to mean both a shekel and an agora, so nothing stopped a value
 * in one unit reaching a parameter expecting the other — and both directions
 * are off by a factor of 100:
 *
 *     lineTotalAgorot(ils(23.5), 1.19)  →   2797  = ₪27.97   correct
 *     lineTotalAgorot(2350,      1.19)  → 279700  = ₪2797    100× too much
 *
 * Both compiled silently. Naming helps (`totalAgorot`) but it was carrying 21
 * identifiers against 488 unit-less ones — a convention, not a guarantee.
 *
 * So the units are branded types. A plain `number` cannot enter a money
 * function; `ils()` / `agorot()` are the only doors in, and each one is a
 * deliberate statement about which unit you hold. Branded values flow OUT as
 * ordinary numbers (`Agorot` IS a `number`), so callers that merely display or
 * store the result need no change at all — the strictness is only on the way in.
 *
 * Cost at runtime: zero. It disappears at compile time.
 *
 * ## Where each unit lives
 *
 * - **Shekels** are the working unit of the CART and of every external API
 *   (HYP and EZcount both take 2-decimal shekel prices).
 * - **Agorot** are the storage unit of DOCUMENTS and the LEDGER
 *   (`totalAgorot`, `vatAgorot`, `amountAgorot`), where an integer is required.
 *
 * Conversion happens here and nowhere else.
 */

const AGOROT_PER_ILS = 100;

/** Israeli VAT, percent. */
export const VAT_PERCENT = 18;

/**
 * Round to 2 decimal places (agorot precision), killing float noise.
 *
 * Lives here rather than in `./index` so the money module has no dependency on
 * the cart module — `index` imports this, never the other way round.
 */
export const round2 = (n: number): number => Number(n.toFixed(2));

/**
 * The two units, as distinct types. A plain `number` is assignable to NEITHER,
 * which is the entire point — the compiler refuses a unit mix-up that no amount
 * of care reliably prevents.
 */
export type Agorot = number & { readonly __unit: "agorot" };
export type Ils = number & { readonly __unit: "ils" };

/**
 * Declare that a number is in shekels. The only way a raw number becomes `Ils`,
 * so every one of these is a deliberate claim about the unit — and greppable.
 */
export function ils(value: number): Ils {
	return value as Ils;
}

/**
 * Declare that a number is a whole count of agorot. Use at the edge, for values
 * arriving already in agorot (a stored `totalAgorot`, an external API).
 */
export function agorot(value: number): Agorot {
	return value as Agorot;
}

/**
 * Add money. Adding two `Agorot` with `+` yields a plain `number` — the brand
 * does not survive arithmetic — so summing goes through here instead of being
 * re-asserted with `agorot()` at every call site. Integer addition of integer
 * agorot: nothing to round.
 */
export function sumAgorot(values: Agorot[]): Agorot {
	return values.reduce<number>((total, value) => total + value, 0) as Agorot;
}

/** Shekels → agorot, for storing a document or ledger amount as an integer. */
export function ilsToAgorot(value: Ils): Agorot {
	return Math.round(value * AGOROT_PER_ILS) as Agorot;
}

/** Agorot → shekels, for display and for external APIs that want a number. */
export function agorotToIls(value: Agorot): Ils {
	return (value / AGOROT_PER_ILS) as Ils;
}

/**
 * Agorot → the 2-decimal shekel string an external API or a printed document
 * expects. This is formatting, not arithmetic — nothing may be added to it.
 */
export function formatAgorot(value: Agorot): string {
	return (value / AGOROT_PER_ILS).toFixed(2);
}

/**
 * A cart line's total as an integer number of agorot, for storing on a document
 * or in the ledger.
 *
 * Uses the SAME per-line shape as `chargeLineInclVat` — round the unit to 2dp,
 * multiply by quantity, round the line — so a document line total cannot drift
 * from the charge. It mirrors `lineNet` inside `getCartCost`; the difference is
 * only that this takes whichever price the caller holds (net for a `PRE`
 * document line, gross for an `INC` one) and returns agorot.
 *
 * Rounding the UNIT first is not a stylistic choice: HYP and EZcount both
 * receive a 2-decimal unit price and multiply on their own side, so the unit is
 * rounded whether we model it or not. Modelling it is what stops their answer
 * from differing from ours.
 */
export function lineTotalAgorot(unitPriceIls: Ils, quantity: number): Agorot {
	const unit2dp = round2(unitPriceIls);
	return ilsToAgorot(ils(round2(unit2dp * quantity)));
}

/**
 * True when an external system can be handed `price` and `amount` and cannot
 * possibly disagree with us about the line — because nothing has to be rounded.
 *
 * Two conditions, both required:
 *  1. the unit price is already 2 decimals, so sending it loses nothing;
 *  2. unit × quantity lands on a whole number of agorot, so there is no
 *     fraction for the other side to resolve its own way.
 *
 * A line that fails this is sent to EZcount with the quantity folded into the
 * price (`amount: 1`), which makes its multiplication trivial. Without that,
 * EZcount re-derives the fraction, resolves it its own way, and `auto_balance`
 * silently absorbs the gap as a `price_discount` — the hidden agora that later
 * stops an invoice from closing its parent document (production: DN 70161).
 */
export function isExactLine(unitPriceIls: Ils, quantity: number): boolean {
	const unit2dp = round2(unitPriceIls);
	if (unit2dp !== (unitPriceIls as number)) return false;
	// Integer arithmetic: agorot × thousandths-of-a-unit must divide evenly.
	const unitAgorot = Math.round(unit2dp * AGOROT_PER_ILS);
	const quantityMilli = Math.round(quantity * 1000);
	return (unitAgorot * quantityMilli) % 1000 === 0;
}
